import type { FilterRuleModel, OptimizationIDModel } from '@/shared/generated'

import { useEffect, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { filter, map, matches, prop, sum } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSelectedCurrency } from '@/entities/currency'
import {
	MetricGroup,
	MetricGroupValue,
	MetricName,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'
import { PricingAlertsGlobalWidget } from '@/entities/price-alert'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Operators } from '@/shared/components/query-builder'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/shared/components/ui/accordion'
import { ampli } from '@/shared/lib/ampli'
import { formatNumber } from '@/shared/tools/locale'
import { getFiltersQueryString } from '@/shared/tools/location'

import AverageValuesWidget from './AverageValuesWidget'
import { FinancialInsightsWidget } from './FinancialinsightsWidget'
import ItemsChangedGlobalWidget from './ItemsChangedGlobalWidget'
import PricingLinesChangeTypeWidget from './PricingLinesChangeTypeWidget'
import PromoWidget from './PromoWidget'

export const StatisticsTab = ({
	queryParams,
	isLoading,
}: {
	isLoading: boolean
	queryParams: {
		filters: FilterRuleModel[]
		optimizations: OptimizationIDModel[]
	} | null
}) => {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	useEffect(() => {
		ampli.optSummaryGlobOpenStatisticsTab()
	}, [])
	const navigate = useNavigate()
	const repricingStatisticQuery = useRepricingStatisticQuery(queryParams!, {
		enabled: !!queryParams && !isLoading,
		refetchOnMount: true,
		staleTime: 0,
		select: (data) =>
			data?.metrics.filter(
				matches({
					group_type: MetricGroup.PriceChangeStatus,
					name: MetricName.ProductCount,
				})
			) || [],
	})
	const changed = formatNumber(
		sum(
			map(
				prop('value'),
				filter(
					({ group_value }) =>
						[MetricGroupValue.Increased, MetricGroupValue.Decreased].includes(
							group_value as MetricGroupValue
						),
					repricingStatisticQuery.data
				)
			)
		) || 0
	)
	const total = sum(map(prop('value'), repricingStatisticQuery.data))
	const filters = useMemo(
		() => [
			{
				name: 'optimization_group_id',
				operation: Operators.IN as const,
				value: map(
					prop('optimization_group_id'),
					queryParams?.optimizations || []
				),
			},
		],
		[queryParams]
	)
	const handleNavigate = ({
		widgetFilter,
		widget,
	}: {
		widgetFilter: FilterRuleModel[]
		widget: string
	}) => {
		ampli.optSummaryGlobPromoWidgetClickRow({
			filters: [...widgetFilter, ...filters].map((f) => f.name),
		})
		navigate({
			to: `${location.pathname}${getFiltersQueryString([...widgetFilter, ...filters])}`,
		})
	}

	return (
		<div className='divide-base flex w-full flex-col divide-y divide-solid px-4 pt-3 pb-8'>
			<ErrorBoundary>
				<Accordion
					defaultValue={['alerts', 'insights', 'skus', 'pls', 'promo', 'avg']}
					type='multiple'
					className='w-full'
				>
					<AccordionItem value='alerts'>
						<AccordionTrigger>{t('alerts')}</AccordionTrigger>
						<AccordionContent>
							<PricingAlertsGlobalWidget currency={currency} />
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='insights'>
						<AccordionTrigger>
							{t('opt.summary.financial_insights.title', {
								defaultValue: 'Financial insights',
							})}
						</AccordionTrigger>
						<AccordionContent>
							<FinancialInsightsWidget
								isLoading={isLoading}
								queryParams={queryParams}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='skus'>
						<AccordionTrigger>
							<div className='grid gap-1'>
								<h5 className='text-base'>{t('skus.changed')}</h5>
								<p className='text-sm font-normal text-muted-foreground'>
									{t('skus.changed.subtitle', {
										changed,
										total,
										defaultValue: `${changed} of ${total} SKUs were optimized`,
									})}
								</p>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<ItemsChangedGlobalWidget
								isLoading={isLoading}
								queryParams={queryParams}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='pls'>
						<AccordionTrigger>{t('pls.change_types')}</AccordionTrigger>
						<AccordionContent>
							<PricingLinesChangeTypeWidget
								isLoading={isLoading}
								queryParams={queryParams}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='promo'>
						<AccordionTrigger>
							{t('app.promo', { defaultValue: 'Promo' })}
						</AccordionTrigger>
						<AccordionContent>
							<PromoWidget
								isLoading={isLoading}
								queryParams={queryParams}
								handleNavigate={handleNavigate}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='avg'>
						<AccordionTrigger>{t('average_values')}</AccordionTrigger>
						<AccordionContent>
							<AverageValuesWidget
								isLoading={isLoading}
								queryParams={queryParams}
							/>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</ErrorBoundary>
		</div>
	)
}
